/**
 * Frontend copy of the built-in layout (backend: models/reportcard.layout.js).
 * Used for old published snapshots that were saved before layouts existed.
 */
export const DEFAULT_LAYOUT = {
  version: 1,
  settings: {
    title: "",
    font_size: 11,
    show_logo: true,
    show_school_address: true,
    show_border: true,
    border_style: "double",
    border_width: 3,
    border_color: "#1d3557",
    border_radius: 4,
  },
  blocks: [
    { id: "header", type: "HEADER", visible: true },
    { id: "student", type: "STUDENT_INFO", visible: true, title: "",
      fields: ["student_name", "class", "father_name", "sr_no", "mother_name", "roll_no", "date_of_birth"] },
    { id: "scholastic", type: "SCHOLASTIC", visible: true, title: "Scholastic Area",
      show_theory_practical: true, show_grand_total: true, show_percentage: true, show_result: true },
    { id: "co_scholastic", type: "CO_SCHOLASTIC", visible: true, title: "Co-Scholastic Area [on a 5 point (A–E) grading scale]" },
    { id: "footer", type: "FOOTER", visible: true, title: "" },
    { id: "signatures", type: "SIGNATURES", visible: true, labels: ["Date", "Class Teacher's Signature", "Principal's Signature"] },
    { id: "legend", type: "GRADE_LEGEND", visible: true,
      title: "Instruction: Grades are awarded on an 8-point & 5-point grading scale",
      show_scholastic: true, show_co_scholastic: true },
  ],
};

export const BLOCK_INFO = {
  HEADER: { label: "School Header", icon: "fa-school", core: true },
  STUDENT_INFO: { label: "Student Details", icon: "fa-id-card", core: true },
  SCHOLASTIC: { label: "Marks Table (Scholastic)", icon: "fa-table", core: true },
  CO_SCHOLASTIC: { label: "Co-Scholastic Grades", icon: "fa-table-list", core: true },
  FOOTER: { label: "Footer Fields (Attendance, Remarks…)", icon: "fa-list", core: true },
  SIGNATURES: { label: "Signatures", icon: "fa-signature" },
  GRADE_LEGEND: { label: "Grade Legend / Instruction", icon: "fa-circle-info", core: true },
  TEXT: { label: "Custom Text", icon: "fa-font" },
  SPACER: { label: "Blank Space", icon: "fa-arrows-up-down" },
};

export const STUDENT_FIELDS = [
  { key: "student_name", label: "Student's Name" },
  { key: "class", label: "Class" },
  { key: "father_name", label: "Father's Name" },
  { key: "sr_no", label: "S.R. No." },
  { key: "mother_name", label: "Mother's Name" },
  { key: "roll_no", label: "Roll No." },
  { key: "date_of_birth", label: "Date of Birth" },
];

/** Sample report used by the layout designer preview */
export const SAMPLE_REPORT = (() => {
  const g = (p) => (p > 90 ? "A1" : p > 80 ? "A2" : p > 70 ? "B1" : p > 60 ? "B2" : "C1");
  const subs = [
    ["English", 80, 20, 68, 17],
    ["Economics", 80, 20, 70, 18],
    ["Political Science", 80, 20, 75, 19],
    ["IP/PE", 70, 30, 60, 26],
    ["Painting", 30, 70, 25, 65],
  ];
  return {
    report_type: "ANNUAL",
    issue_date: new Date().toISOString().slice(0, 10),
    result_status: "PASS",
    student: {
      student_name: "Sample Student",
      father_name: "Father Name",
      mother_name: "Mother Name",
      class_name: "XI",
      section_name: "A",
      sr_no: "SR101",
      roll_no: "1",
      date_of_birth: "01-05-2010",
      session_year: "2026-2027",
    },
    columns: [
      {
        key: "G1",
        name: "Term 1",
        exam_titles: [
          { id: "t1_hy", name: "Half Yearly" },
          { id: "t1_pt", name: "Pt-I" },
        ],
      },
      {
        key: "G2",
        name: "Term 2",
        exam_titles: [
          { id: "t2_ann", name: "Annual" },
        ],
      },
    ],
    scholastic: {
      subjects: subs.map(([n, tm, pm, to, po]) => {
        const pt_to = Math.round(to * 0.25);
        const t1_tot_max = tm + pm + 20;
        const t1_tot_obt = to + po + pt_to;
        const t2_tot_max = tm + pm;
        const t2_tot_obt = to + po - 5;
        return {
          name: n,
          columns: {
            G1: {
              theory: { max: tm + 20, obt: to + pt_to },
              practical: { max: pm, obt: po },
              total: { max: t1_tot_max, obt: t1_tot_obt },
              has_theory: true,
              has_practical: true,
              grade: g(((t1_tot_obt) / t1_tot_max) * 100),
              titles: {
                t1_hy: {
                  theory: { max: tm, obt: to },
                  practical: { max: pm, obt: po },
                  total: { max: tm + pm, obt: to + po },
                  has_theory: true,
                  has_practical: true,
                  absent: false,
                },
                t1_pt: {
                  theory: { max: 20, obt: pt_to },
                  total: { max: 20, obt: pt_to },
                  has_theory: true,
                  absent: false,
                },
              },
            },
            G2: {
              theory: { max: tm, obt: to - 5 },
              practical: { max: pm, obt: po },
              total: { max: t2_tot_max, obt: t2_tot_obt },
              has_theory: true,
              has_practical: true,
              grade: g(((t2_tot_obt) / t2_tot_max) * 100),
              titles: {
                t2_ann: {
                  theory: { max: tm, obt: to - 5 },
                  practical: { max: pm, obt: po },
                  total: { max: t2_tot_max, obt: t2_tot_obt },
                  has_theory: true,
                  has_practical: true,
                  absent: false,
                },
              },
            },
          },
          grand_total: {
            total: { obt: t1_tot_obt + t2_tot_obt, max: t1_tot_max + t2_tot_max },
            grade: g(((t1_tot_obt + t2_tot_obt) / (t1_tot_max + t2_tot_max)) * 100),
          },
        };
      }),
      totals: {
        columns: {
          G1: {
            obt: 518,
            max: 600,
            grade: "A2",
            titles: {
              t1_hy: { theory: { max: 340, obt: 288 }, practical: { max: 160, obt: 145 }, max: 500, obt: 433 },
              t1_pt: { theory: { max: 100, obt: 85 }, max: 100, obt: 85 },
            },
          },
          G2: {
            obt: 408,
            max: 500,
            grade: "A2",
            titles: {
              t2_ann: { theory: { max: 340, obt: 263 }, practical: { max: 160, obt: 145 }, max: 500, obt: 408 },
            },
          },
        },
        grand_total: { obt: 926, max: 1100, percentage: 84.18, grade: "A2" },
      },
    },
    co_scholastic: {
      columns: [{ key: "G1", name: "Term 1" }, { key: "G2", name: "Term 2" }],
      subjects: [
        { id: 1, name: "Physical & Health Education", grades: { G1: "A", G2: "A" } },
        { id: 2, name: "Work Experience", grades: { G1: "B", G2: "A" } },
      ],
    },
    footer: [
      { field_key: "ATTENDANCE", title: "Attendance", value: "180 / 200" },
      { field_key: "PROMOTED_CLASS", title: "Promoted to Class", value: "XII" },
      { field_key: "TEACHER_REMARK", title: "Class Teacher's Remarks", value: "Hard working and sincere.", input_type: "TEXTAREA" },
    ],
    grade_legend: {
      scholastic: [["A1", 90.01, 100], ["A2", 80.01, 90], ["B1", 70.01, 80], ["B2", 60.01, 70], ["C1", 50.01, 60], ["C2", 40.01, 50], ["D", 32.01, 40], ["E", 0, 32]]
        .map(([grade, from, to]) => ({ grade, from, to })),
      co_scholastic: [["A", 80.01, 100], ["B", 60.01, 80], ["C", 40.01, 60], ["D", 32.01, 40], ["E", 0, 32]].map(([grade, from, to]) => ({ grade, from, to })),
    },
  };
})();
